import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Container,
  Paper,
  InputAdornment,
} from "@mui/material";
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getDomainStatus } from "../api/APICalls";
import RecordDisplay from "../components/RecordDisplay";
import MailFlowDisplay from "../components/MailFlowDisplay";
import LanguageIcon from "@mui/icons-material/Language";
import SearchIcon from "@mui/icons-material/Search";

const HomePage = () => {
  const [domain, setDomain] = React.useState("");
  const [searchDomain, setSearchDomain] = React.useState("");

  const { data: domainDetails, error, isLoading, refetch } = useQuery({
    queryKey: ["domainStatus", searchDomain],
    queryFn: () => getDomainStatus(searchDomain),
    enabled: !!searchDomain,
    retry: false,
  });

  const statusHandleSubmit = (e) => {
    e.preventDefault();
    if (!domain) return;
    localStorage.setItem("domain", domain);
    setSearchDomain(domain);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mb: 6,
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 800,
            background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textAlign: "center",
            mb: 1,
          }}
        >
          Mail Service Monitor
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4, textAlign: "center", maxWidth: 600 }}>
          Comprehensive self-test suite for domain mail health and delivery performance.
        </Typography>

        <Paper
          component="form"
          onSubmit={statusHandleSubmit}
          elevation={0}
          sx={{
            p: "4px 8px",
            display: "flex",
            alignItems: "center",
            width: "100%",
            maxWidth: 500,
            borderRadius: 10,
            border: "2px solid #e0e0e0",
            transition: "all 0.3s",
            "&:focus-within": {
              borderColor: "primary.main",
              boxShadow: "0 0 15px rgba(33, 150, 243, 0.2)",
            },
          }}
        >
          <TextField
            fullWidth
            placeholder="Enter domain name (e.g., example.com)"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start" sx={{ ml: 1 }}>
                  <LanguageIcon color="action" />
                </InputAdornment>
              ),
              sx: { py: 1, px: 1 },
            }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || !domain}
            sx={{
              borderRadius: 8,
              px: 4,
              minWidth: 120,
              boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : "Check Status"}
          </Button>
        </Paper>
      </Box>

      {error && (
        <Paper
          sx={{
            p: 3,
            mb: 4,
            bgcolor: "error.lighter",
            border: "1px solid",
            borderColor: "error.light",
            borderRadius: 3,
            textAlign: "center"
          }}
        >
          <Typography variant="body1" color="error.main" sx={{ fontWeight: 600 }}>
            {error.message}
          </Typography>
        </Paper>
      )}

      {domainDetails && (
        <Box sx={{ mb: 6 }}>
          <RecordDisplay data={domainDetails} />
          {domainDetails.active && (
            <Box sx={{ mt: 6 }}>
              <MailFlowDisplay />
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
};

export default HomePage;
